import { Badge, makeStyles, mergeClasses, tokens } from '@fluentui/react-components';
import { brand } from '@/shared/styles/colors';
import type { BadgeProps } from '@fluentui/react-components';

export type AppBadgeVariant =
  | 'success'   // brand.mountainGreen — low risk/urgency/priority, live, approved
  | 'warning'   // brand.wasatchSunrise — medium, in-review statuses, pending review
  | 'high'      // brand.coralPink — high risk/priority (salmon)
  | 'danger'    // brand.redDarker — high urgency, rejected/denied
  | 'critical'  // brand.red — critical tier, brand red (categories, tools)
  | 'info'      // brand.lakePowell — awaiting/active statuses, blue dollars
  | 'amber'     // brand.aspenYellow — on-hold, appealed, waived
  | 'neutral';  // brand.saltFlat — draft, neutral, data types

const useStyles = makeStyles({
  base: {
    fontFamily: brand.fontFamily,
    fontWeight: tokens.fontWeightMedium,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
  },
  success:  { backgroundColor: brand.mountainGreen, color: brand.white },
  warning:  { backgroundColor: brand.wasatchSunrise, color: brand.bodyText },
  high:     { backgroundColor: brand.coralPink, color: brand.white },
  danger:   { backgroundColor: brand.redDarker, color: brand.white },
  critical: { backgroundColor: brand.red, color: brand.white },
  info:     { backgroundColor: brand.lakePowell, color: brand.white },
  amber:    { backgroundColor: brand.aspenYellow, color: brand.bodyText },
  neutral:  { backgroundColor: brand.saltFlat, color: brand.bodyText },
});

interface AppBadgeProps extends Omit<BadgeProps, 'appearance' | 'color'> {
  variant?: AppBadgeVariant;
  className?: string;
}

export function AppBadge({ variant = 'neutral', className, ...props }: AppBadgeProps) {
  const styles = useStyles();
  return (
    <Badge
      appearance="filled"
      shape="rounded"
      className={mergeClasses(styles.base, styles[variant], className)}
      {...props}
    />
  );
}

export default AppBadge;
